import { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PageHead from "@/components/PageHead";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { GraduationCap, Sparkles, Award, BookOpen, Compass, MessageSquare, ArrowUpRight, Download, HeartHandshake, X, Printer } from "lucide-react";
import AiInsights from "@/components/schools/AiInsights";
import DemoFlow from "@/components/schools/DemoFlow";
import SchoolKpi from "@/components/schools/SchoolKpi";
import {
  student, studentDimensions, studentCareerMatches, studentTimeline, aiInsightsStudent,
  counsellorQueue,
} from "@/data/greenwoodPersonas";
import { downloadCsv } from "@/lib/csv";
import { writeLastViewedStudent, readLastViewedStudent } from "@/hooks/useLastViewedStudent";

// Cohort benchmarks — Grade 10 median at Greenwood.
const cohortBenchmarks = { hdi: 74, cci: 71, fri: 66, portfolioItems: 6 } as const;

const iconFor: Record<string, React.ReactNode> = {
  assessment: <GraduationCap className="w-3.5 h-3.5" />,
  badge: <Award className="w-3.5 h-3.5" />,
  reflection: <MessageSquare className="w-3.5 h-3.5" />,
  module: <BookOpen className="w-3.5 h-3.5" />,
  mentor: <Sparkles className="w-3.5 h-3.5" />,
};

export default function StudentProfile() {
  const [params] = useSearchParams();
  const referredId = params.get("student") ?? readLastViewedStudent();
  const referred = referredId ? counsellorQueue.find((s) => s.id === referredId) : null;
  const initials = student.name.split(" ").map(s => s[0]).slice(0, 2).join("");

  // Persist the last-viewed student so RoleSwitcher can deep-link back here.
  useEffect(() => {
    const id = params.get("student");
    if (id) writeLastViewedStudent(id);
  }, [params]);

  const exportStudent = () => {
    const rows: (string | number)[][] = [
      ["WeKIT Education Intelligence™ — Student snapshot"],
      ["Student", student.name],
      ["Roll", student.rollNo],
      ["Grade", `${student.grade}-${student.section}`],
      ["Mentor", student.mentor],
      ["Generated", new Date().toISOString()],
      [],
      ["Index", "Value", "Cohort median"],
      ["Human Development Index™", student.hdi, cohortBenchmarks.hdi],
      ["Career Clarity Index™", student.cci, cohortBenchmarks.cci],
      ["Future Readiness Index™", student.fri, cohortBenchmarks.fri],
      ["Portfolio artifacts", student.portfolioItems, cohortBenchmarks.portfolioItems],
      [],
      ["Dimension", "Score"],
      ...studentDimensions.map((d) => [d.dim, d.score]),
      [],
      ["Career match", "Cluster", "Fit %"],
      ...studentCareerMatches.map((c) => [c.title, c.cluster, c.fit]),
    ];
    downloadCsv(`${student.name.toLowerCase().replace(/\s+/g, "-")}-snapshot-${new Date().toISOString().slice(0, 10)}.csv`, rows);
  };

  return (
    <>
      <PageHead title={`${student.name} | Student Profile`} description="Learner profile for WeKIT Education Intelligence™" path="/schools/student" />
      <div className="container max-w-7xl py-8 space-y-10">
        {referred && (
          <div
            role="status"
            className="flex flex-wrap items-center gap-3 rounded-2xl border border-primary/20 bg-primary/5 px-4 py-3 text-sm"
          >
            <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary grid place-items-center shrink-0">
              <HeartHandshake className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">
                Opened from Counsellor queue · {referred.name} · Grade {referred.grade}
              </div>
              <div className="text-xs text-muted-foreground truncate">
                Signal: {referred.signal} · Priority: {referred.priority}
              </div>
            </div>
            <Badge variant="secondary" className="rounded-full text-[10px] uppercase tracking-wider">
              Demo: showing Aarav's data
            </Badge>
            <Button asChild size="sm" variant="ghost">
              <Link to="/schools/counsellor" aria-label="Back to counsellor queue">
                <X className="w-3.5 h-3.5 mr-1" /> Clear
              </Link>
            </Button>
          </div>
        )}
        {/* Header */}
        <header className="flex flex-wrap items-center gap-6">
          <Avatar className="w-16 h-16">
            <AvatarFallback className="bg-primary/10 text-primary text-lg font-semibold">{initials}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wider mb-1">
              <Sparkles className="w-3.5 h-3.5" /> Student Profile · Grade {student.grade}-{student.section}
            </div>
            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">{student.name}</h1>
            <p className="text-muted-foreground mt-1">
              Roll {student.rollNo} · {student.house} House · Mentor {student.mentor}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge variant="secondary" className="rounded-full">🔥 {student.reflectionStreak}-day streak</Badge>
            <Badge variant="secondary" className="rounded-full">{student.badges} badges</Badge>
            <Button size="sm" variant="outline" onClick={exportStudent}>
              <Download className="w-3.5 h-3.5 mr-1" /> Export CSV
            </Button>
            <Button size="sm" variant="outline" asChild>
              <Link to="/schools/student/onepager"><Printer className="w-3.5 h-3.5 mr-1" /> One-pager</Link>
            </Button>
            <Button size="sm" variant="outline" asChild>
              <Link to="/schools/portfolio">Open Portfolio <ArrowUpRight className="w-3.5 h-3.5 ml-1" /></Link>
            </Button>
          </div>
        </header>

        {/* KPIs */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <SchoolKpi variant="north-star" infoKey="hdi" label="Human Development Index™" value={student.hdi} hint="Top 15% of cohort" tone="positive" series={[74, 76, 79, 81, 83, 85, 86]} benchmark={cohortBenchmarks.hdi} />
          <SchoolKpi variant="north-star" infoKey="cci" label="Career Clarity Index™"    value={student.cci} hint="+8 this term"        tone="positive" series={[68, 70, 72, 75, 78, 80, 82]} benchmark={cohortBenchmarks.cci} />
          <SchoolKpi infoKey="fri" label="Future Readiness Index™"  value={student.fri} hint="Strong AI + Creativity" tone="positive" series={[64, 66, 68, 71, 73, 76, 78]} benchmark={cohortBenchmarks.fri} />
          <SchoolKpi infoKey="portfolio" label="Portfolio artifacts"      value={student.portfolioItems} hint={`${student.badges} badges earned`} series={[2, 3, 4, 5, 6, 8, 9]} benchmark={cohortBenchmarks.portfolioItems} />
        </section>

        <div className="grid lg:grid-cols-3 gap-4">
          {/* Dimensions */}
          <Card className="lg:col-span-2">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Human Development dimensions</CardTitle>
              <p className="text-xs text-muted-foreground">Where {student.name.split(" ")[0]} is strongest today</p>
            </CardHeader>
            <CardContent className="grid sm:grid-cols-2 gap-4">
              {studentDimensions.map((d) => (
                <div key={d.dim} className="space-y-1.5">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium">{d.dim}</span>
                    <span className="tabular-nums text-muted-foreground">{d.score}</span>
                  </div>
                  <Progress value={d.score} className="h-1.5" />
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Career matches */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><Compass className="w-4 h-4 text-primary" /> Top career matches</CardTitle>
              <p className="text-xs text-muted-foreground">Best-fit clusters this month</p>
            </CardHeader>
            <CardContent className="space-y-3">
              {studentCareerMatches.map((c) => (
                <div key={c.title} className="flex items-center justify-between gap-3 rounded-xl border p-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{c.title}</div>
                    <div className="text-xs text-muted-foreground truncate">{c.cluster}</div>
                  </div>
                  <div className="text-sm tabular-nums font-semibold text-primary">{c.fit}%</div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Timeline */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Recent activity</CardTitle>
            <p className="text-xs text-muted-foreground">Latest learning, reflections and mentor moments</p>
          </CardHeader>
          <CardContent>
            <ul className="divide-y">
              {studentTimeline.map((t, i) => (
                <li key={i} className="flex items-center gap-3 py-3">
                  <div className="w-7 h-7 rounded-lg bg-primary/10 text-primary grid place-items-center shrink-0">
                    {iconFor[t.type]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{t.title}</div>
                    <div className="text-xs text-muted-foreground">{t.date}</div>
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <AiInsights
          insights={aiInsightsStudent}
          title="AI insights for this learner"
          subtitle="Signals from the last 30 days"
          sendToParent={{
            subject: `WeKIT insight — ${student.name}`,
            intro: `Hi ${student.name.split(" ")[0]}'s family,\n\nSharing a signal from this week's WeKIT Career Clarity 360™ view:`,
            signature: `— ${student.mentor}, mentor · WeKIT Education Intelligence™\nOpen the family dashboard: /schools/parent`,
          }}
        />
        <DemoFlow />
      </div>
    </>
  );
}
